<template>
  <v-menu v-model="menu" width="100%" :close-on-content-click="true" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-model="searchString"
        v-bind="attrs"
        v-on="on"
        class="searchPlaces"
        :placeholder="
          $router.currentRoute.name === 'home'
            ? $t('label.iSearch')
            : `${$t('label.search')} / ${$t('objekt.objektNumber')}`
        "
        hide-details
        append-icon="mdi-magnify"
        @click:append="goToFirst"
        @keyup="updateSearchString"
        @click:clear="clear"
        @focus="loadData"
        full-width
        single-line
        clearable
        outlined
        dense
        :loading="loadingSearchData"
      />
    </template>

    <v-sheet class="resultBox" v-if="results.length">
      <v-list-item v-if="results.length && results[0].error" class="pa-1 pl-3" text left flat>
        {{ results[0].error }}
      </v-list-item>
      <v-list dense v-else>
        <v-list-item v-for="(result, index) in results" :key="index" @click="goToResult(result)">
          <v-icon class="mr-2">{{ getIconForType(result.type) }}</v-icon>
          <span style="white-space: normal">
            {{ getNameForResult(result) }}
          </span>
        </v-list-item>
      </v-list>
    </v-sheet>
  </v-menu>
</template>

<script>
import { debounce } from 'debounce';
import Axios from 'axios';
import { applyTransform, boundingExtent } from 'ol/extent';
import { getTransform } from 'ol/proj';
import Kampagnen from '@/models/kampagnen';
import Objekt from '@/models/objekt';
import { getCenter } from 'ol/extent';
import FMMatrix from '@/models/fm_matrix';
import Glossar from '@/models/glossar';
import { tags } from '@/main/tags';
import { loader } from '@/mixins/loader';
import { foerderungenLoader } from '@/mixins/foerderungenLoader';
import { kampagnenLoader } from '@/mixins/kampagnenLoader';
import { bevSearch } from '@/utils/utils';
import { defaultFilterValues } from '@/store/defaultValues';

export default {
  mixins: [loader, foerderungenLoader, kampagnenLoader],
  props: {
    objektClicked: {
      type: Function,
      default: () => () => {}
    }
  },
  data: () => ({
    searchString: '',
    loadingSearchData: false,
    results: [],
    menu: false
  }),
  mounted() {
    this.debouncedSeach = debounce(this.search, 250);
    this.debouncedTextlog = debounce(this.postTextLog, 1000);
  },
  methods: {
    loadData() {
      this.load(true);
      this.loadFoerderungen();
      this.loadKampagnen();
    },
    updateSearchString(e) {
      if (this.searchString && this.searchString.length > 2) {
        this.debouncedSeach();
        if (e.key !== 'Enter') {
          this.debouncedTextlog();
        }
      } else {
        this.loadingSearchData = false;
        this.results = [];
      }
    },
    clear() {
      this.results.length = 0;
    },
    async postTextLog() {
      if (this.searchString?.length) {
        const payload = {
          suchtext: this.searchString,
          logtype: this.$route.name === 'home' ? 'start' : 'standort'
        };
        Axios.post('/api/textlog', payload);
      }
    },
    async search() {
      const userlevel = this.$store.state.userdata?.userlevel || 0;
      // 180620002: 'Anzeigen mit Login',
      // 180620003: 'Anzeigen ohne Login',
      const allowedStatus =
        userlevel === 0
          ? [180620003]
          : this.$store.state.userdata.userlevel > 6
          ? defaultFilterValues.selectedStatus
          : [180620002, 180620003];
      const allObjekte = Objekt.query()
        .where(objekt => {
          return !!allowedStatus.includes(objekt.status);
        })
        .get();

      // objektnummer-search
      if (this.searchString.length && !isNaN(Number(this.searchString))) {
        const filteredObjekte = allObjekte.filter(o => {
          //@ts-ignore
          return o.objektnummer.toString().includes(this.searchString);
        });
        this.results = filteredObjekte.map(o => {
          return {
            //@ts-ignore
            objektnummer: o.objektnummer,
            //@ts-ignore
            id: o.id,
            //@ts-ignore
            flaechenbezeichnung: o.flaechenbezeichnung,
            //@ts-ignore
            type: o.objektart,
            searchType: 'objektnummer'
          };
        });
        return;
      }
      //"objektname" bzw. "objektname_en" und in "flaechenbezeichnung",
      // use searchstring to find objekte via objektname, objektname_en  and flaechenbezeichnung
      // each word has to be found
      const searchProps = ['flaechenbezeichnung'];
      if (this.$route.params.lang === 'en') {
        searchProps.push('objektname_en');
      } else {
        searchProps.push('objektname');
      }
      const searchWords = this.searchString.toLowerCase().replace(/\s\s+/g, ' ').split(' ');
      const filteredObjekte = [];

      const lang = this.$route.params.lang;
      const maxObjekte = this.$route.name === 'home' ? 5 : 15;
      for (let i = 0, ii = allObjekte.length; i < ii; i++) {
        const objekt = allObjekte[i];
        let compoundSearchableText = searchProps
          //@ts-ignore
          .map(p => objekt[p] || objekt.uebersetzung[p])
          .join('')
          .toLowerCase();

        //@ts-ignore
        if (objekt.standortbeschreibung && objekt.standortbeschreibung.length) {
          //@ts-ignore
          objekt.standortbeschreibung.forEach(id => {
            compoundSearchableText += tags.find(t => t.id === id)[lang].toLowerCase();
          });
        }
        if (searchWords.every(word => compoundSearchableText.includes(word))) {
          filteredObjekte.push(objekt);
        }
        if (filteredObjekte.length === maxObjekte) {
          break;
        }
      }

      this.loadingSearchData = true;

      try {
        const results = await bevSearch(this.searchString, {
          lang: this.$route.params.lang
        });
        this.results.length = 0;
        this.results.push(...results);

        this.results.push(
          ...filteredObjekte.map(o => {
            return {
              //@ts-ignore
              objektnummer: o.objektnummer,
              //@ts-ignore
              id: o.id,
              //@ts-ignore
              flaechenbezeichnung: o.flaechenbezeichnung,
              //@ts-ignore
              type: o.objektart
            };
          })
        );

        const searchLowerCase = this.searchString.toLowerCase();
        if (this.$route.name === 'home') {
          // in the home component, also search Wissen, Förderkompass and Glossar
          const kampagnenResults = Kampagnen.query() // only german
            .where(kampagne => {
              return !!(kampagne.aktiv === true && kampagne.modul1text?.toLowerCase().includes(searchLowerCase));
            })
            .orWhere(kampagne => {
              return !!(kampagne.aktiv === true && kampagne.modul2text?.toLowerCase().includes(searchLowerCase));
            })
            .orWhere(kampagne => {
              return !!(kampagne.aktiv === true && kampagne.modul3text?.toLowerCase().includes(searchLowerCase));
            })
            .orWhere(kampagne => {
              return !!(kampagne.aktiv === true && kampagne.modul4text?.toLowerCase().includes(searchLowerCase));
            })
            .orWhere(kampagne => {
              return !!(kampagne.aktiv === true && kampagne.modul5text?.toLowerCase().includes(searchLowerCase));
            })
            .orWhere(kampagne => {
              return !!(kampagne.aktiv === true && kampagne.modul6text?.toLowerCase().includes(searchLowerCase));
            })
            .orWhere(kampagne => {
              return !!(
                kampagne.aktiv === true && kampagne.veranstaltungentext?.toLowerCase().includes(searchLowerCase)
              );
            })
            .limit(5)
            .get();

          this.results.push(
            ...kampagnenResults.map(k => {
              return {
                //@ts-ignore
                id: k.id,
                //@ts-ignore
                flaechenbezeichnung: k.header,
                //@ts-ignore
                type: 'kampagne'
              };
            })
          );

          // Förderkompass
          const langAppendix = this.$route.params.lang === 'en' ? '_en' : '_de';
          const fKompassResults = FMMatrix.query()
            .where(foerderung => {
              return !!foerderung[`name${langAppendix}`]?.toLowerCase().includes(searchLowerCase);
            })
            .orWhere(foerderung => {
              return !!foerderung[`zielgruppe${langAppendix}`]?.toLowerCase().includes(searchLowerCase);
            })
            .orWhere(foerderung => {
              return !!foerderung[`kurzbeschreibung${langAppendix}`]?.toLowerCase().includes(searchLowerCase);
            })
            .orWhere(foerderung => {
              return !!foerderung[`detailbeschreibung${langAppendix}`]?.toLowerCase().includes(searchLowerCase);
            })
            .limit(5)
            .get();
          this.results.push(
            ...fKompassResults.map(k => {
              return {
                //@ts-ignore
                id: k.id,
                //@ts-ignore
                flaechenbezeichnung: k[`name${langAppendix}`],
                //@ts-ignore
                type: 'fKompass'
              };
            })
          );

          const glossarResults = Glossar.query()
            .where(glos => {
              return !!glos[`titel${langAppendix}`]?.toLowerCase().includes(searchLowerCase);
            })
            .orWhere(glos => {
              return !!glos[`beschreibung${langAppendix}`]?.toLowerCase().includes(searchLowerCase);
            })
            .limit(5)
            .get();
          this.results.push(
            ...glossarResults.map(k => {
              return {
                //@ts-ignore
                id: k.id,
                //@ts-ignore
                flaechenbezeichnung: k[`titel${langAppendix}`],
                //@ts-ignore
                type: 'glossar'
              };
            })
          );
        }
        if (!this.results.length) {
          this.results = [{ error: 'Keine Ergebnisse' }];
        }
        this.loadingSearchData = false;
      } catch (err) {
        this.results = [{ error: 'Fehler bei der Ortssuche' }];
        this.loadingSearchData = false;
      }
    },

    getIconForType(type) {
      if (type === 'Feature') {
        return 'mdi-map-marker';
      } else if (type === 180620000) {
        return 'mdi-selection';
      } else if (type === 180620001) {
        return 'mdi-office-building';
      } else if (type === 180620002) {
        return 'mdi-factory';
      } else if (type === 'kampagne') {
        return 'mdi-newspaper-variant';
      } else if (type === 'fKompass') {
        return 'mdi-currency-eur';
      } else if (type === 'glossar') {
        return 'mdi-message-processing';
      }
    },

    goToResult(result) {
      if (result.objektnummer) {
        // ecoplus objekt, directly go to objekt page
        this.objektClicked(result.id);
        return;
      }

      if (result.type === 'kampagne') {
        this.$router.push(`/${this.$route.params.lang}/wissen/${result.id}`);
        return;
      }
      if (result.type === 'fKompass') {
        this.$router.push(`/${this.$route.params.lang}/foerderkompass/${result.id}`);
        return;
      }
      if (result.type === 'glossar') {
        this.$router.push(`/${this.$route.params.lang}/glossar/${result.id}`);
        return;
      }

      let extent = result.extent;
      const resultText = this.getNameForResult(result);
      this.results.length = 0;
      this.searchString = resultText;

      if (!extent) {
        extent = boundingExtent([result.geometry.coordinates]);
      }
      extent = [
        Math.min(extent[0], extent[2]),
        Math.min(extent[1], extent[3]),
        Math.max(extent[0], extent[2]),
        Math.max(extent[1], extent[3])
      ];
      if (this.$route.name === 'home') {
        const center = getCenter(extent);
        const newQuery = Object.assign({}, this.$route.query, { zoom: 11, center: center.join(',') });
        this.$router.push({ name: 'standortsuche', query: newQuery });
      } else {
        const mercatorExtent = applyTransform(extent, getTransform('EPSG:4326', 'EPSG:3857'));
        this.$map.getView().fit(mercatorExtent, {
          padding: [50, 50, 50, 50],
          duration: 250,
          maxZoom: 15
        });
      }
    },

    getNameForResult(result) {
      if (result.type === 'Feature') {
        // BEV result
        return result.properties.name;
      } else {
        // ecoplus-objekte, found via objektnummer
        if (result.searchType === 'objektnummer') {
          return `Objekt ${result.objektnummer} (${result.flaechenbezeichnung})`;
        } else {
          return result.flaechenbezeichnung;
        }
      }
    },
    goToFirst() {
      if (this.results.length && !this.results[0].error) {
        this.goToResult(this.results[0]);
      }
    }
  }
};
</script>

<style scoped>
.searchPlaces {
  background-color: white;
  margin-bottom: 0px;
  padding-top: 0px;
  border-radius: 0px;
}

.resultBox {
  max-height: 200px;
}
</style>
